import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Print as PrintIcon, Payments as PaymentsIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../../../../context";
import { addObject, getObjectsWithParams } from "../../../../../api/Api";
import { getReceiptHeaderInfo } from "../../../../../utils/hospitalInfo";

const formatMoney = (value) => Number(value || 0).toLocaleString();

export default function CollectNICUFees() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";

  const [searchTerm, setSearchTerm] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectingId, setCollectingId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [collectForm, setCollectForm] = useState({
    subtotal_amount: "",
    discount_amount: "",
  });

  const normalizedSearch = searchTerm.trim();

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page_size: 50,
      };
      if (normalizedSearch) {
        params.search = normalizedSearch;
      }
      const response = await getObjectsWithParams(
        "baby-payments",
        params,
        token
      );
      setPayments(response?.results || response || []);
    } catch (error) {
      setMessage({
        type: "error",
        text: t("finance.nicuFetchError", { defaultValue: "Unable to load NICU fees." }),
      });
    } finally {
      setLoading(false);
    }
  }, [normalizedSearch, t, token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const summary = useMemo(() => {
    return payments.reduce(
      (acc, item) => {
        acc.total += Number(item.total_amount || 0);
        acc.discount += Number(item.discount_amount || 0);
        acc.count += 1;
        return acc;
      },
      { total: 0, discount: 0, count: 0 }
    );
  }, [payments]);

  const handleCollect = async () => {
    if (!selectedPayment || collectingId !== null) {
      return;
    }

    try {
      const paymentId = selectedPayment.id;
      setCollectingId(paymentId);
      await addObject(
        `baby-payments/${paymentId}/mark-paid`,
        {
          subtotal_amount: collectForm.subtotal_amount || 0,
          discount_amount: collectForm.discount_amount || 0,
        },
        token
      );
      setMessage({
        type: "success",
        text: t("finance.nicuCollectSuccess", { defaultValue: "NICU payment collected successfully." }),
      });
      setConfirmOpen(false);
      setSelectedPayment(null);
      setCollectForm({ subtotal_amount: "", discount_amount: "" });
      await fetchPayments();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.detail || t("finance.nicuCollectError", { defaultValue: "Unable to collect NICU payment." }),
      });
    } finally {
      setCollectingId(null);
    }
  };

  const printReceipt = (payment) => {
    const { hospitalName, hospitalAddress, hospitalPhone, phoneLabel } = getReceiptHeaderInfo(t);
    const receiptWindow = window.open("", "_blank");
    if (!receiptWindow) {
      return;
    }

    const html = `
      <html>
        <head>
          <title>NICU Receipt</title>
          <style>
            @page {
              size: A5 portrait;
              margin: 12mm;
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              color: #111;
              margin: 0;
              background: #fff;
            }
            .receipt {
              width: 100%;
              min-height: 100%;
            }
            .meta {
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 11px;
              margin-bottom: 18px;
            }
            .header {
              text-align: center;
              margin-bottom: 18px;
            }
            .title {
              font-size: 18px;
              font-weight: 700;
              margin-bottom: 4px;
            }
            .small {
              font-size: 11px;
              line-height: 1.4;
            }
            .details {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .details td {
              padding: 3px 0;
              font-size: 14px;
              vertical-align: top;
            }
            .details td:first-child {
              width: 38%;
            }
            .details td:last-child {
              width: 62%;
              text-align: right;
            }
            .spacer td {
              padding-top: 10px;
            }
            .divider td {
              padding-top: 6px;
              border-bottom: 1px solid #cfcfcf;
            }
            .total-row td {
              padding-top: 8px;
              font-size: 16px;
              font-weight: 700;
            }
            .notes {
              margin-top: 10px;
              font-size: 14px;
              font-weight: 700;
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="meta">
              <div>${new Date().toLocaleDateString()}${", "}${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              <div>NICU Receipt</div>
              <div></div>
            </div>

            <div class="header">
              <div class="title">${hospitalName}</div>
              ${hospitalAddress ? `<div class="small">${hospitalAddress}</div>` : ""}
              ${hospitalPhone ? `<div class="small">${phoneLabel}: ${hospitalPhone}</div>` : ""}
              <div class="small">NICU Fees Receipt</div>
            </div>

            <table class="details">
              <tr><td>Ticket</td><td>${payment.ticket_number || "-"}</td></tr>
              <tr><td>Patient Name</td><td>${payment.patient_name || "-"}</td></tr>
              <tr><td>Patient ID</td><td>${payment.patient_id || "-"}</td></tr>
              <tr><td>Refer By</td><td>${payment.refer_by || "-"}</td></tr>
              <tr><td>Status</td><td>${payment.status || "-"}</td></tr>

              <tr class="spacer"><td>Sub Total</td><td>${formatMoney(payment.subtotal_amount)}</td></tr>
              <tr><td>Discount</td><td>${formatMoney(payment.discount_amount)}</td></tr>
              <tr class="divider"><td colspan="2"></td></tr>
              <tr class="total-row"><td>Total Amount</td><td>${formatMoney(payment.total_amount)}</td></tr>
            </table>

            <div class="notes">Notes: ${payment.notes || "-"}</div>
          </div>
        </body>
      </html>
    `;

    receiptWindow.document.write(html);
    receiptWindow.document.close();
    receiptWindow.focus();
    receiptWindow.print();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>
        {t("finance.nicuFees", { defaultValue: "NICU Fees" })}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t("finance.nicuFeesDesc", { defaultValue: "Search patient ticket, collect NICU fees, and print receipt." })}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label={t("finance.nicuSearchTicket", { defaultValue: "Search Patient Ticket" })}
            placeholder={t("finance.nicuSearchTicketPlaceholder", { defaultValue: "Type at least 3 characters, e.g. 000" })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Alert severity="info">
            {normalizedSearch
              ? t("finance.nicuSearchActive", { defaultValue: "Showing matching NICU patients." })
              : t("finance.nicuShowingAll", { defaultValue: "Showing all NICU patients. You can also search by ticket." })}
          </Alert>

          {message.text ? (
            <Alert severity={message.type === "error" ? "error" : "success"} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
              <Typography variant="body2">
                {t("finance.records", { defaultValue: "Records" })}: <strong>{summary.count}</strong>
              </Typography>
              <Typography variant="body2">
                {t("finance.discount", { defaultValue: "Discount" })}: <strong>{formatMoney(summary.discount)}</strong>
              </Typography>
              <Typography variant="body2">
                {t("finance.totalAmount", { defaultValue: "Total Amount" })}: <strong>{formatMoney(summary.total)}</strong>
              </Typography>
            </Stack>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("finance.ticket", { defaultValue: "Ticket" })}</TableCell>
                  <TableCell>{t("finance.patientName", { defaultValue: "Patient Name" })}</TableCell>
                  <TableCell>{t("finance.patientId", { defaultValue: "Patient ID" })}</TableCell>
                  <TableCell>{t("finance.status", { defaultValue: "Status" })}</TableCell>
                  <TableCell>{t("finance.discount", { defaultValue: "Discount" })}</TableCell>
                  <TableCell>{t("finance.amount", { defaultValue: "Amount" })}</TableCell>
                  <TableCell align="right">{t("finance.actions", { defaultValue: "Actions" })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : payments.length > 0 ? (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{payment.ticket_number || "-"}</TableCell>
                      <TableCell>{payment.patient_name || "-"}</TableCell>
                      <TableCell>{payment.patient_id || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status || (payment.is_paid ? "Paid" : "Pending")}
                          color={payment.is_paid ? "success" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatMoney(payment.discount_amount)}</TableCell>
                      <TableCell>{formatMoney(payment.is_paid ? 0 : payment.total_amount)}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => printReceipt(payment)}>
                          <PrintIcon />
                        </IconButton>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={collectingId === payment.id ? <CircularProgress color="inherit" size={16} /> : <PaymentsIcon />}
                          disabled={payment.is_paid || collectingId === payment.id}
                          onClick={() => {
                            setSelectedPayment(payment);
                            setCollectForm({
                              subtotal_amount: payment.subtotal_amount || payment.total_amount || "",
                              discount_amount: payment.discount_amount || "",
                            });
                            setConfirmOpen(true);
                          }}
                        >
                          {payment.is_paid
                            ? t("finance.collected", { defaultValue: "Collected" })
                            : t("finance.collect", { defaultValue: "Collect" })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : normalizedSearch ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t("finance.noMatchingPatient", { defaultValue: "No matching NICU payment found." })}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t("finance.noNICUPatients", { defaultValue: "No NICU patients found." })}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("finance.collectNICUPayment", { defaultValue: "Collect NICU Payment" })}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t("finance.collectNICUPaymentPrompt", {
              defaultValue: "Do you want to collect this NICU payment and add it to total revenue?",
            })}
          </Typography>
          {selectedPayment ? (
            <Box mt={2}>
              <Typography variant="body2">
                <strong>{t("finance.patientName", { defaultValue: "Patient Name" })}:</strong> {selectedPayment.patient_name || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>{t("finance.ticket", { defaultValue: "Ticket" })}:</strong> {selectedPayment.ticket_number || "-"}
              </Typography>
              <Box mt={2}>
                <TextField
                  fullWidth
                  margin="dense"
                  label={t("finance.amount", { defaultValue: "Amount" })}
                  value={collectForm.subtotal_amount}
                  onChange={(e) =>
                    setCollectForm((prev) => ({ ...prev, subtotal_amount: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  margin="dense"
                  label={t("finance.discount", { defaultValue: "Discount" })}
                  value={collectForm.discount_amount}
                  onChange={(e) =>
                    setCollectForm((prev) => ({ ...prev, discount_amount: e.target.value }))
                  }
                />
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={collectingId !== null}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleCollect}
            disabled={collectingId !== null}
            startIcon={collectingId !== null ? <CircularProgress color="inherit" size={16} /> : <PaymentsIcon />}
          >
            {t("finance.collect", { defaultValue: "Collect" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
